
import type { PropType } from 'vue'
import { computed, defineComponent, ref, watch } from 'vue'

import { ColumnOrientation } from '../enums'

import { EpbBadge } from './shared/epb-badge'
import { EpbText } from './shared/epb-text'

export default defineComponent({
  name: 'CollapsableLayout',
  components: {
    EpbBadge,
    EpbText,
  },
  props: {
    hideSideWidth: {
      type: Number,
      default: 0,
    },
    hasSeparator: {
      type: Boolean,
      default: true,
    },
    verticalTitle: String,
    isCollapsed: Boolean,
    collapsibleOrientation: {
      type: Number as PropType<ColumnOrientation>,
      default: ColumnOrientation.LEFT,
    },
    collapseButtonPosition: {
      type: String as PropType<'top' | 'middle' | 'bottom'>,
      default: 'top',
    },
  },
  setup(props: {
    verticalTitle: string
    isCollapsed: boolean
    collapsibleOrientation: number
    hideSideWidth: number
  }) {
    const baseClass = 'epb-collapsable-layout'
    const rightSectionCollapsed = ref(
      props.collapsibleOrientation === ColumnOrientation.RIGHT && props.isCollapsed,
    )
    const leftSectionCollapsed = ref(
      props.collapsibleOrientation === ColumnOrientation.LEFT && props.isCollapsed,
    )

    const dynamicArrow = computed(() => {
      if (props.collapsibleOrientation === ColumnOrientation.RIGHT) {
        return rightSectionCollapsed.value ? 'chevronleftbig' : 'chevronrightbig'
      }

      return leftSectionCollapsed.value ? 'chevronrightbig' : 'chevronleftbig'
    })

    const collapseSettings = function (): void {
      if (props.collapsibleOrientation === ColumnOrientation.RIGHT) {
        rightSectionCollapsed.value = !rightSectionCollapsed.value
      } else {
        leftSectionCollapsed.value = !leftSectionCollapsed.value
      }
    }

    const getColumnClass = function (
      column: ColumnOrientation,
    ): Array<string | Record<string, any>> {
      if (column === ColumnOrientation.RIGHT) {
        return [
          `${baseClass}__column`,
          {
            [`epb-grid-col-md-${props.hideSideWidth}`]:
              props.collapsibleOrientation === ColumnOrientation.RIGHT && !!props.hideSideWidth,
            [`${baseClass}__column--hide`]: rightSectionCollapsed.value,
            [`${baseClass}__column--collapsed`]:
              props.collapsibleOrientation === ColumnOrientation.RIGHT,
            [`${baseClass}__column--expanded`]:
              props.collapsibleOrientation === ColumnOrientation.LEFT,
          },
        ]
      }

      return [
        `${baseClass}__column`,
        {
          [`epb-grid-col-md-${props.hideSideWidth}`]:
            props.collapsibleOrientation === ColumnOrientation.LEFT && !!props.hideSideWidth,
          [`${baseClass}__column--fixed`]: props.hideSideWidth === -1,
          [`${baseClass}__column--hide`]: leftSectionCollapsed.value,
          [`${baseClass}__column--collapsed`]:
            props.collapsibleOrientation === ColumnOrientation.LEFT,
          [`${baseClass}__column--expanded`]:
            props.collapsibleOrientation === ColumnOrientation.RIGHT,
        },
      ]
    }

    watch(
      (): boolean => props.isCollapsed,
      (): void => collapseSettings(),
      { deep: true },
    )

    return {
      baseClass,
      collapseSettings,
      dynamicArrow,
      ColumnOrientation,
      rightSectionCollapsed,
      leftSectionCollapsed,
      getColumnClass,
    }
  },
})
